import React, { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  IconButton,
  Chip,
  Tooltip,
  Checkbox,
  TableSortLabel,
  Button,
} from '@mui/material';
import {
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import type { Email } from '../../types/api';
import { formatKategorie } from './utils';

type Order = 'asc' | 'desc';
type OrderBy = 'absenderAnzeige' | 'betreffText' | 'empfangenAm' | 'status';

interface EmailTableProps {
  emails: Email[];
  mailKonten: { emailAdresse?: string }[];
  onShowDetails: (email: Email) => void;
  onMarkAsRead: (email: Email, read: boolean) => void;
  onReply: (email: Email) => void;
  onArchive: (email: Email) => void;
  onDelete: (emailId: number) => void;
  onDeleteMultiple?: (emailIds: number[]) => void;
}

const EmailTable: React.FC<EmailTableProps> = ({
  emails,
  mailKonten,
  onShowDetails,
  onMarkAsRead,
  onReply,
  onArchive,
  onDelete,
  onDeleteMultiple,
}) => {
  // State für Mehrfachauswahl
  const [selected, setSelected] = useState<number[]>([]);

  // State für Sortierung
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('empfangenAm');

  // Prüft, ob eine E-Mail von einem eigenen Konto gesendet wurde
  const isEmailSent = (email: Email): boolean => {
    if (!email.absenderEmail) return false;
    return mailKonten.some(konto =>
      konto.emailAdresse?.toLowerCase() === email.absenderEmail?.toLowerCase()
    );
  };

  // Sortierung Handler
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Checkbox Handler
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(emails.map((email) => email.emailID));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (emailId: number) => {
    const selectedIndex = selected.indexOf(emailId);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, emailId];
    } else {
      newSelected = selected.filter((id) => id !== emailId);
    }

    setSelected(newSelected);
  };

  const isSelected = (emailId: number) => selected.indexOf(emailId) !== -1;

  // Mehrfach löschen
  const handleDeleteSelected = () => {
    if (selected.length === 0) return;

    if (!window.confirm(`${selected.length} E-Mail(s) wirklich löschen?`)) return;

    if (onDeleteMultiple) {
      onDeleteMultiple(selected);
    } else {
      // Fallback: Einzeln löschen
      selected.forEach((id) => onDelete(id));
    }
    setSelected([]);
  };

  // Sortierte E-Mails
  const sortedEmails = useMemo(() => {
    const comparator = (a: Email, b: Email): number => {
      let valueA: string | number | boolean;
      let valueB: string | number | boolean;

      switch (orderBy) {
        case 'absenderAnzeige':
          valueA = a.absenderAnzeige?.toLowerCase() || '';
          valueB = b.absenderAnzeige?.toLowerCase() || '';
          break;
        case 'betreffText':
          valueA = a.betreffText?.toLowerCase() || '';
          valueB = b.betreffText?.toLowerCase() || '';
          break;
        case 'empfangenAm':
          valueA = new Date(a.empfangenAm).getTime();
          valueB = new Date(b.empfangenAm).getTime();
          break;
        case 'status':
          valueA = a.gelesen ? 1 : 0;
          valueB = b.gelesen ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
      return 0;
    };

    return [...emails].sort(comparator);
  }, [emails, order, orderBy]);

  return (
    <TableContainer component={Paper}>
      {/* Toolbar für Mehrfachauswahl */}
      {selected.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
          }}
        >
          <Typography variant="subtitle1">
            {selected.length} E-Mail(s) ausgewählt
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteSelected}
          >
            Ausgewählte löschen
          </Button>
        </Box>
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={selected.length > 0 && selected.length < emails.length}
                checked={emails.length > 0 && selected.length === emails.length}
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'status'}
                direction={orderBy === 'status' ? order : 'asc'}
                onClick={() => handleRequestSort('status')}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'absenderAnzeige'}
                direction={orderBy === 'absenderAnzeige' ? order : 'asc'}
                onClick={() => handleRequestSort('absenderAnzeige')}
              >
                Von
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'betreffText'}
                direction={orderBy === 'betreffText' ? order : 'asc'}
                onClick={() => handleRequestSort('betreffText')}
              >
                Betreff
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'empfangenAm'}
                direction={orderBy === 'empfangenAm' ? order : 'asc'}
                onClick={() => handleRequestSort('empfangenAm')}
              >
                Empfangen
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEmails.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="textSecondary">
                  Keine E-Mails vorhanden
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortedEmails.map((email) => {
              const isItemSelected = isSelected(email.emailID);
              return (
              <TableRow
                key={email.emailID}
                hover
                onClick={() => onShowDetails(email)}
                selected={isItemSelected}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: email.gelesen ? 'inherit' : 'action.hover',
                  fontWeight: email.gelesen ? 'normal' : 'bold',
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isItemSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(email.emailID);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    {isEmailSent(email) && (
                      <Chip
                        icon={<SendIcon sx={{ fontSize: 14 }} />}
                        label="Versendet"
                        color="info"
                        size="small"
                        sx={{ height: 20 }}
                      />
                    )}
                    {!email.gelesen && (
                      <Chip
                        label="Neu"
                        color="primary"
                        size="small"
                        sx={{ height: 20 }}
                      />
                    )}
                    {email.wichtig && (
                      <Chip
                        label="!"
                        color="error"
                        size="small"
                        sx={{ height: 20, minWidth: 20 }}
                      />
                    )}
                    {email.beantwortet && (
                      <Chip
                        label="Beantwortet"
                        color="success"
                        size="small"
                        sx={{ height: 20 }}
                      />
                    )}
                    {email.analysiert && email.kategorie && (
                      <Chip
                        label={formatKategorie(email.kategorie)}
                        color={
                          email.kategorie === 'Wichtig' ? 'error' :
                          email.kategorie === 'ZurKenntnis' ? 'info' : 'default'
                        }
                        size="small"
                        sx={{ height: 20 }}
                      />
                    )}
                    {email.hasAttachments && (
                      <AttachFileIcon fontSize="small" color="action" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {email.istGesendet && (
                      <SendIcon fontSize="small" color="primary" titleAccess="Gesendet" />
                    )}
                    <Typography
                      variant="body2"
                      fontWeight={email.gelesen ? 'normal' : 'bold'}
                    >
                      {email.istGesendet
                        ? `An: ${email.empfaengerEmail || 'Unbekannt'}`
                        : email.absenderAnzeige}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight={email.gelesen ? 'normal' : 'bold'}
                  >
                    {email.betreffText}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {new Date(email.empfangenAm).toLocaleString('de-DE')}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Tooltip
                      title={
                        email.gelesen
                          ? 'Als ungelesen markieren'
                          : 'Als gelesen markieren'
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkAsRead(email, !email.gelesen);
                        }}
                      >
                        {email.gelesen ? (
                          <MarkUnreadIcon fontSize="small" />
                        ) : (
                          <MarkReadIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Antworten">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onReply(email);
                        }}
                      >
                        <ReplyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="In Paperless archivieren">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onArchive(email);
                        }}
                      >
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(email.emailID);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EmailTable;
